import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * System Logs & Monitoring
 *
 * Tabs: All Activity | API Logs | User Activity | Admin Actions | Errors
 * Server-side pagination, caching, smart auto-refresh, expandable rows,
 * category/user/level/endpoint filters, export, stats bar.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { FaSync, FaDownload, FaExclamationTriangle, FaInfoCircle, FaChevronRight, FaChevronLeft, FaUser, FaShieldAlt, FaServer, FaSearch, FaPause, FaPlay, FaClock, FaSignInAlt, FaSignOutAlt, FaEye, FaExchangeAlt } from 'react-icons/fa';
import { apiClient } from '../../utils/apiClient';
import { activityTracker } from '../../services/activityTracker';
import { SuperAdminOnly } from '../../components/PermissionGuards';
import { Tabs } from '../../components/shared/Tabs';
import { PageContainer, PageTitle, ContentSection, Button } from '../../components/PageStyles';
// ─── Constants ───────────────────────────────────────────────────
const TAB_CATEGORIES = {
    all: undefined,
    api: 'api',
    'user-activity': 'user-action',
    admin: 'admin',
    errors: 'error'
};
const TABS = [
    { id: 'all', label: 'All Activity' },
    { id: 'api', label: 'API Logs' },
    { id: 'user-activity', label: 'User Activity' },
    { id: 'admin', label: 'Admin Actions' },
    { id: 'errors', label: 'Errors' }
];
const TIME_RANGES = {
    '1h': 60 * 60 * 1000,
    '6h': 6 * 60 * 60 * 1000,
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000
};
const PAGE_SIZE = 50;
const CACHE_TTL = 60000;
const AUTO_REFRESH_INTERVAL = 15000;
// ─── Category colors ─────────────────────────────────────────────
const CATEGORY_COLORS = {
    api: { bg: 'rgba(139, 92, 246, 0.10)', text: '#8b5cf6', border: 'rgba(139, 92, 246, 0.25)' },
    auth: { bg: 'rgba(245, 158, 11, 0.10)', text: '#d97706', border: 'rgba(245, 158, 11, 0.25)' },
    'user-action': { bg: 'rgba(20, 184, 166, 0.10)', text: '#0d9488', border: 'rgba(20, 184, 166, 0.25)' },
    admin: { bg: 'rgba(249, 115, 22, 0.10)', text: '#ea580c', border: 'rgba(249, 115, 22, 0.25)' },
    error: { bg: 'rgba(239, 68, 68, 0.10)', text: '#dc2626', border: 'rgba(239, 68, 68, 0.25)' },
};
const LEVEL_COLORS = {
    error: '#ef4444',
    warn: '#f59e0b',
    info: '#22c55e',
    debug: '#6b7280',
};
// ─── Helpers ─────────────────────────────────────────────────────
function formatTimestamp(ts) {
    const d = new Date(ts);
    const month = d.toLocaleString('en-US', { month: 'short' });
    const day = d.getDate();
    const time = d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true });
    return `${month} ${day}, ${time}`;
}
function getDurationColor(ms) {
    if (ms < 500)
        return '#22c55e';
    if (ms < 2000)
        return '#f59e0b';
    return '#ef4444';
}
function getCatColor(category) {
    return CATEGORY_COLORS[category] || CATEGORY_COLORS.api;
}
function getActionIcon(log) {
    if (log.action === 'page-view')
        return _jsx(FaEye, {});
    if (log.action === 'api-call')
        return _jsx(FaExchangeAlt, {});
    if (log.action === 'login' || log.action === 'user-logged-in' || log.action === 'login_success')
        return _jsx(FaSignInAlt, {});
    if (log.action === 'logout' || log.action === 'user-logged-out')
        return _jsx(FaSignOutAlt, {});
    if (log.category === 'api')
        return _jsx(FaServer, {});
    if (log.category === 'auth')
        return _jsx(FaShieldAlt, {});
    if (log.category === 'admin')
        return _jsx(FaShieldAlt, {});
    if (log.category === 'error')
        return _jsx(FaExclamationTriangle, {});
    return _jsx(FaUser, {});
}
// ─── Smart message renderer ──────────────────────────────────────
function renderLogContent(log) {
    // API call: show structured method + endpoint + status
    if (log.category === 'api' && log.action === 'api-call') {
        const method = log.metadata?.method || 'GET';
        const ep = log.endpoint || log.metadata?.endpoint || '';
        const status = log.statusCode ?? log.metadata?.statusCode;
        const dur = log.duration ?? log.metadata?.duration;
        return (_jsxs(LogContentRow, { children: [_jsx(MethodChip, { "$method": method, children: method }), _jsx(EndpointText, { children: ep }), status != null && (_jsx(StatusChip, { "$ok": status < 400, children: status })), dur != null && (_jsxs(DurationText, { style: { color: getDurationColor(dur) }, children: [dur, "ms"] }))] }));
    }
    // Page view
    if (log.action === 'page-view') {
        return (_jsxs(LogContentRow, { children: [_jsx(ActionLabel, { children: "Page viewed" }), _jsx(EndpointText, { children: log.endpoint || log.message.replace('Viewed ', '') })] }));
    }
    // Auth login
    if (log.action === 'login' || log.action === 'user-logged-in' || log.action === 'login_success') {
        return (_jsxs(LogContentRow, { children: [_jsx(ActionLabel, { children: "Login" }), _jsx("span", { style: { color: 'var(--text-secondary)' }, children: log.userEmail || log.metadata?.email || 'Unknown user' }), log.metadata?.role && _jsx(RoleChip, { children: log.metadata.role })] }));
    }
    // Auth logout
    if (log.action === 'logout' || log.action === 'user-logged-out') {
        return (_jsxs(LogContentRow, { children: [_jsx(ActionLabel, { children: "Logout" }), _jsx("span", { style: { color: 'var(--text-secondary)' }, children: log.userEmail || 'Unknown user' })] }));
    }
    // Admin action
    if (log.category === 'admin') {
        return (_jsxs(LogContentRow, { children: [_jsx(ActionLabel, { children: log.action?.replace(/-/g, ' ') || 'Admin action' }), _jsx("span", { style: { color: 'var(--text-secondary)' }, children: log.message })] }));
    }
    // Fallback: raw message
    return (_jsx(LogContentRow, { children: _jsx("span", { style: { color: 'var(--text-primary)' }, children: log.message }) }));
}
// ─── Component ──────────────────────────────────────────────────
const SystemLogs = () => {
    const [activeTab, setActiveTab] = useState('all');
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ page: 1, pageSize: PAGE_SIZE, totalCount: 0, totalPages: 0 });
    const [levelFilter, setLevelFilter] = useState('all');
    const [userFilter, setUserFilter] = useState('');
    const [endpointFilter, setEndpointFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [timeRange, setTimeRange] = useState('24h');
    const [includeApi, setIncludeApi] = useState(false); // Hide API logs by default
    const [usersList, setUsersList] = useState([]);
    const [stats, setStats] = useState({});
    const [categoryStats, setCategoryStats] = useState({});
    const [autoRefresh, setAutoRefresh] = useState(true);
    const autoRefreshRef = useRef(null);
    const latestTimestampRef = useRef('');
    const [newEntriesCount, setNewEntriesCount] = useState(0);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const cacheRef = useRef(new Map());
    const getCacheKey = useCallback((page) => {
        return `${activeTab}|${levelFilter}|${userFilter}|${endpointFilter}|${timeRange}|${searchTerm}|${page}`;
    }, [activeTab, levelFilter, userFilter, endpointFilter, timeRange, searchTerm]);
    const loadLogs = useCallback(async (page, afterTimestamp) => {
        try {
            if (!afterTimestamp)
                setIsLoading(true);
            setError('');
            const params = new URLSearchParams();
            params.append('page', page.toString());
            params.append('pageSize', PAGE_SIZE.toString());
            const category = TAB_CATEGORIES[activeTab];
            if (category)
                params.append('category', category);
            if (levelFilter !== 'all')
                params.append('level', levelFilter);
            if (userFilter)
                params.append('userId', userFilter);
            if (endpointFilter !== 'all')
                params.append('endpoint', endpointFilter);
            if (searchTerm)
                params.append('search', searchTerm);
            if (afterTimestamp)
                params.append('afterTimestamp', afterTimestamp);
            const startDate = new Date(Date.now() - TIME_RANGES[timeRange]).toISOString();
            params.append('startDate', startDate);
            const response = await apiClient.get(`logs-query?${params.toString()}`);
            if (response.ok && response.data?.success) {
                const data = response.data;
                if (afterTimestamp) {
                    const newLogs = (data.logs || []).filter((l) => {
                        if (!l.userId || l.userId === 'anonymous')
                            return false;
                        if (!includeApi && l.category === 'api')
                            return false;
                        return true;
                    });
                    if (newLogs.length > 0) {
                        if (page === 1) {
                            setLogs(prev => [...newLogs, ...prev]);
                            latestTimestampRef.current = newLogs[0].timestamp;
                        }
                        else {
                            setNewEntriesCount(prev => prev + newLogs.length);
                        }
                    }
                }
                else {
                    // Filter out anonymous/unauthenticated entries and optionally API logs
                    const filtered = (data.logs || []).filter((l) => {
                        if (!l.userId || l.userId === 'anonymous')
                            return false;
                        if (!includeApi && l.category === 'api')
                            return false;
                        return true;
                    });
                    setLogs(filtered);
                    setPagination(data.pagination || { page: 1, pageSize: PAGE_SIZE, totalCount: 0, totalPages: 0 });
                    setStats(data.stats || {});
                    setCategoryStats(data.categoryStats || {});
                    if (data.users)
                        setUsersList(data.users);
                    if (data.logs.length > 0) {
                        latestTimestampRef.current = data.logs[0].timestamp;
                    }
                    const key = getCacheKey(page);
                    cacheRef.current.set(key, {
                        logs: data.logs,
                        pagination: data.pagination,
                        stats: data.stats,
                        categoryStats: data.categoryStats,
                        users: data.users || usersList,
                        timestamp: Date.now()
                    });
                }
            }
            else {
                setError(response.error || 'Failed to load logs');
            }
        }
        catch (err) {
            setError('Failed to load system logs');
        }
        finally {
            setIsLoading(false);
        }
    }, [activeTab, levelFilter, userFilter, endpointFilter, searchTerm, timeRange, getCacheKey, usersList]);
    const loadWithCache = useCallback((page) => {
        const key = getCacheKey(page);
        const cached = cacheRef.current.get(key);
        if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
            setLogs(cached.logs);
            setPagination(cached.pagination);
            setStats(cached.stats);
            setCategoryStats(cached.categoryStats);
            if (cached.users.length > 0)
                setUsersList(cached.users);
            loadLogs(page);
        }
        else {
            loadLogs(page);
        }
    }, [getCacheKey, loadLogs]);
    useEffect(() => {
        setCurrentPage(1);
        setNewEntriesCount(0);
        setExpandedRows(new Set());
        loadLogs(1);
    }, [activeTab, levelFilter, userFilter, endpointFilter, timeRange, includeApi]);
    const handleSearch = () => { setCurrentPage(1); loadLogs(1); };
    const handlePageChange = (page) => {
        setCurrentPage(page);
        setExpandedRows(new Set());
        loadWithCache(page);
    };
    useEffect(() => {
        if (autoRefreshRef.current)
            clearInterval(autoRefreshRef.current);
        if (autoRefresh && currentPage === 1) {
            autoRefreshRef.current = setInterval(() => {
                if (latestTimestampRef.current)
                    loadLogs(1, latestTimestampRef.current);
            }, AUTO_REFRESH_INTERVAL);
        }
        return () => { if (autoRefreshRef.current)
            clearInterval(autoRefreshRef.current); };
    }, [autoRefresh, currentPage, loadLogs]);
    const toggleRow = (id) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    };
    const downloadLogs = () => {
        const logsText = logs.map(log => `[${log.timestamp}] [${log.level?.toUpperCase()}] [${log.category}] ${log.endpoint || ''} - ${log.message}${log.metadata ? '\n' + JSON.stringify(log.metadata, null, 2) : ''}`).join('\n\n');
        const blob = new Blob([logsText], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `system-logs-${new Date().toISOString()}.txt`;
        a.click();
        URL.revokeObjectURL(url);
        activityTracker.trackAdminAction('exported-data', 'Exported system logs', { type: 'logs', count: logs.length });
    };
    const uniqueEndpoints = Array.from(new Set(logs.map(l => l.endpoint).filter(Boolean)));
    const getPageRange = () => {
        const { totalPages } = pagination;
        if (totalPages <= 7)
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        const pages = [1];
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        if (start > 2)
            pages.push('...');
        for (let i = start; i <= end; i++)
            pages.push(i);
        if (end < totalPages - 1)
            pages.push('...');
        pages.push(totalPages);
        return pages;
    };
    const totalLogs = pagination.totalCount;
    return (_jsx(SuperAdminOnly, { children: _jsxs(PageContainer, { children: [_jsx(PageTitle, { children: "System Logs & Monitoring" }), _jsx("p", { style: { color: 'var(--text-secondary)', marginBottom: '24px' }, children: "Activity logs, API calls, admin actions, and error monitoring" }), _jsxs(StatsBar, { children: [_jsxs(StatCard, { children: [_jsx("div", { className: "stat-label", children: "Total Logs" }), _jsx("div", { className: "stat-value", children: totalLogs.toLocaleString() })] }), _jsxs(StatCard, { "$borderColor": "#ef4444", children: [_jsx("div", { className: "stat-label", children: "Errors" }), _jsx("div", { className: "stat-value", style: { color: '#ef4444' }, children: stats.error || 0 })] }), _jsxs(StatCard, { "$borderColor": "#f59e0b", children: [_jsx("div", { className: "stat-label", children: "Warnings" }), _jsx("div", { className: "stat-value", style: { color: '#f59e0b' }, children: stats.warn || 0 })] }), _jsxs(StatCard, { "$borderColor": "#22c55e", children: [_jsx("div", { className: "stat-label", children: "Info" }), _jsx("div", { className: "stat-value", style: { color: '#22c55e' }, children: stats.info || 0 })] })] }), _jsx(Tabs, { tabs: TABS, activeTab: activeTab, onTabChange: setActiveTab }), _jsx(ContentSection, { children: _jsxs(LogsContainer, { children: [_jsxs(LogsHeader, { children: [_jsxs("div", { className: "header-left", children: [_jsx("h3", { style: { margin: 0, color: 'var(--text-primary)', fontSize: '16px', fontWeight: 600 }, children: TABS.find(t => t.id === activeTab)?.label || 'Logs' }), autoRefresh && currentPage === 1 && (_jsxs(RefreshIndicator, { children: [_jsx(LiveDot, {}), "Auto-refresh ", AUTO_REFRESH_INTERVAL / 1000, "s"] })), newEntriesCount > 0 && currentPage > 1 && (_jsxs(NewEntriesBadge, { onClick: () => handlePageChange(1), children: [newEntriesCount, " new ", newEntriesCount === 1 ? 'entry' : 'entries'] }))] }), _jsxs("div", { className: "header-right", children: [_jsxs(HeaderBtn, { onClick: () => setAutoRefresh(!autoRefresh), title: autoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh', children: [autoRefresh ? _jsx(FaPause, {}) : _jsx(FaPlay, {}), autoRefresh ? 'Pause' : 'Resume'] }), _jsxs(HeaderBtn, { onClick: () => loadLogs(currentPage), disabled: isLoading, children: [_jsx(FaSync, { style: { animation: isLoading ? 'spin 1s linear infinite' : 'none' } }), "Refresh"] }), _jsxs(HeaderBtn, { onClick: downloadLogs, disabled: logs.length === 0, "$primary": true, children: [_jsx(FaDownload, {}), "Export"] })] })] }), _jsxs(FilterBar, { children: [_jsxs(Select, { value: levelFilter, onChange: e => setLevelFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All Levels" }), _jsx("option", { value: "error", children: "Errors" }), _jsx("option", { value: "warn", children: "Warnings" }), _jsx("option", { value: "info", children: "Info" })] }), _jsxs(Select, { value: userFilter, onChange: e => setUserFilter(e.target.value), children: [_jsx("option", { value: "", children: "All Users" }), usersList.map(u => (_jsx("option", { value: u.userId, children: u.email || u.userId }, u.userId)))] }), _jsxs(Select, { value: endpointFilter, onChange: e => setEndpointFilter(e.target.value), children: [_jsx("option", { value: "all", children: "All Endpoints" }), uniqueEndpoints.map(ep => (_jsx("option", { value: ep, children: ep }, ep)))] }), _jsxs(Select, { value: timeRange, onChange: e => setTimeRange(e.target.value), children: [_jsx("option", { value: "1h", children: "Last Hour" }), _jsx("option", { value: "6h", children: "Last 6 Hours" }), _jsx("option", { value: "24h", children: "Last 24 Hours" }), _jsx("option", { value: "7d", children: "Last 7 Days" }), _jsx("option", { value: "30d", children: "Last 30 Days" })] }), _jsxs(SearchWrapper, { children: [_jsx(FaSearch, {}), _jsx(SearchInput, { type: "text", placeholder: "Search logs...", value: searchTerm, onChange: e => setSearchTerm(e.target.value), onKeyPress: e => e.key === 'Enter' && handleSearch() })] }), _jsxs(CheckboxWrapper, { children: [_jsx("input", { type: "checkbox", id: "include-api", checked: includeApi, onChange: e => setIncludeApi(e.target.checked) }), _jsx("label", { htmlFor: "include-api", children: "Include API Logs" })] })] }), _jsxs(LogsList, { children: [isLoading && logs.length === 0 && (_jsxs(EmptyState, { children: [_jsx(FaSync, { style: { animation: 'spin 1s linear infinite' } }), _jsx("h3", { children: "Loading logs..." })] })), error && (_jsxs(EmptyState, { children: [_jsx(FaExclamationTriangle, {}), _jsx("h3", { children: "Error Loading Logs" }), _jsx("p", { children: error }), _jsx(Button, { onClick: () => loadLogs(currentPage), style: { marginTop: '16px' }, children: "Retry" })] })), !isLoading && !error && logs.length === 0 && (_jsxs(EmptyState, { children: [_jsx(FaInfoCircle, {}), _jsx("h3", { children: "No Logs Found" }), _jsx("p", { children: "Try adjusting your filters or time range" })] })), logs.map(log => {
                                        const isExpanded = expandedRows.has(log.id);
                                        const catColor = getCatColor(log.category);
                                        return (_jsxs(LogEntryRow, { "$level": log.level, onClick: () => toggleRow(log.id), children: [_jsxs(LogRowMain, { children: [_jsxs(LogRowLeft, { children: [_jsx(ExpandIcon, { "$expanded": isExpanded, children: _jsx(FaChevronRight, {}) }), _jsx(LevelDot, { "$color": LEVEL_COLORS[log.level] || '#6b7280', title: log.level }), _jsx(ActionIcon, { "$category": log.category, children: getActionIcon(log) }), _jsx(CategoryPill, { style: { background: catColor.bg, color: catColor.text, borderColor: catColor.border }, children: log.category === 'user-action' ? 'user' : log.category }), _jsx(LogContentWrap, { children: renderLogContent(log) })] }), _jsxs(LogRowRight, { children: [(log.userEmail || log.userId) && (_jsx(UserLabel, { title: log.userEmail || log.userId || '', children: log.userEmail ? log.userEmail.split('@')[0] : log.userId ? log.userId.slice(0, 8) : '' })), _jsxs(TimestampLabel, { children: [_jsx(FaClock, {}), formatTimestamp(log.timestamp)] })] })] }), isExpanded && (_jsxs(LogExpandedDetails, { onClick: e => e.stopPropagation(), children: [_jsxs(DetailGrid, { children: [_jsxs(DetailSection, { children: [_jsx(DetailHeading, { children: "Request Details" }), log.action && _jsxs(DetailRow, { children: [_jsx(DetailKey, { children: "Action" }), _jsx(DetailVal, { children: log.action })] }), log.endpoint && _jsxs(DetailRow, { children: [_jsx(DetailKey, { children: "Endpoint" }), _jsx(DetailVal, { children: _jsx("code", { children: log.endpoint }) })] }), log.duration != null && _jsxs(DetailRow, { children: [_jsx(DetailKey, { children: "Duration" }), _jsxs(DetailVal, { style: { color: getDurationColor(log.duration) }, children: [log.duration, "ms"] })] }), log.statusCode != null && _jsxs(DetailRow, { children: [_jsx(DetailKey, { children: "Status" }), _jsx(DetailVal, { children: _jsx(StatusChip, { "$ok": log.statusCode < 400, children: log.statusCode }) })] }), _jsxs(DetailRow, { children: [_jsx(DetailKey, { children: "Log ID" }), _jsx(DetailVal, { children: _jsx("code", { style: { fontSize: '11px' }, children: log.id }) })] })] }), _jsxs(DetailSection, { children: [_jsx(DetailHeading, { children: "User Info" }), log.userEmail && _jsxs(DetailRow, { children: [_jsx(DetailKey, { children: "Email" }), _jsx(DetailVal, { children: log.userEmail })] }), log.userRole && _jsxs(DetailRow, { children: [_jsx(DetailKey, { children: "Role" }), _jsx(DetailVal, { children: _jsx(RoleChip, { children: log.userRole }) })] }), log.userId && _jsxs(DetailRow, { children: [_jsx(DetailKey, { children: "User ID" }), _jsx(DetailVal, { children: _jsx("code", { style: { fontSize: '11px' }, children: log.userId }) })] }), _jsxs(DetailRow, { children: [_jsx(DetailKey, { children: "Timestamp" }), _jsx(DetailVal, { children: new Date(log.timestamp).toISOString() })] })] })] }), log.message && (_jsxs(MessageBlock, { children: [_jsx(DetailHeading, { children: "Message" }), _jsx(MessageText, { children: log.message })] })), log.metadata && Object.keys(log.metadata).length > 0 && (_jsxs(MetadataBlock, { children: [_jsx(DetailHeading, { children: "Metadata" }), _jsx(MetadataPre, { children: JSON.stringify(log.metadata, null, 2) })] }))] }))] }, log.id));
                                    })] }), pagination.totalPages > 1 && (_jsxs(PaginationBar, { children: [_jsxs(PaginationInfo, { children: ["Showing ", ((currentPage - 1) * PAGE_SIZE) + 1, "\u2013", Math.min(currentPage * PAGE_SIZE, pagination.totalCount), " of ", pagination.totalCount.toLocaleString()] }), _jsxs(PageButtons, { children: [_jsx(PageBtn, { disabled: currentPage === 1, onClick: () => handlePageChange(currentPage - 1), children: _jsx(FaChevronLeft, {}) }), getPageRange().map((p, i) => typeof p === 'string' ? (_jsx(PageEllipsis, { children: "..." }, `e-${i}`)) : (_jsx(PageBtn, { "$active": p === currentPage, onClick: () => handlePageChange(p), children: p }, p))), _jsx(PageBtn, { disabled: currentPage === pagination.totalPages, onClick: () => handlePageChange(currentPage + 1), children: _jsx(FaChevronRight, {}) })] })] }))] }) }), _jsx("style", { children: `
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes pulse-dot { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        ` })] }) }));
};
// ─── Styled Components ──────────────────────────────────────────
const StatsBar = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
`;
const StatCard = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-left: 4px solid ${p => p.$borderColor || 'var(--border-color)'};
  padding: 16px;
  border-radius: 4px;
  .stat-label { font-size: 11px; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 8px; }
  .stat-value { font-size: 24px; font-weight: 600; color: var(--text-primary); }
`;
const LogsContainer = styled.div `
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  overflow: hidden;
`;
const LogsHeader = styled.div `
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  .header-left { display: flex; align-items: center; gap: 12px; }
  .header-right { display: flex; gap: 8px; }
`;
const HeaderBtn = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: ${p => p.$primary ? 'var(--text-primary)' : 'transparent'};
  color: ${p => p.$primary ? 'var(--bg-primary)' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) { border-color: var(--text-tertiary); color: ${p => p.$primary ? 'var(--bg-primary)' : 'var(--text-primary)'}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  svg { font-size: 11px; }
`;
const LiveDot = styled.span `
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  animation: pulse-dot 1.5s ease-in-out infinite;
`;
const RefreshIndicator = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-tertiary);
`;
const NewEntriesBadge = styled.button `
  background: #3b82f6;
  color: white;
  border: none;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  animation: pulse-dot 1.5s ease-in-out infinite;
`;
const FilterBar = styled.div `
  padding: 10px 20px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;
const Select = styled.select `
  padding: 7px 10px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  min-width: 120px;
  &:focus { outline: none; border-color: var(--text-tertiary); }
`;
const SearchWrapper = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  flex: 1;
  min-width: 160px;
  svg { color: var(--text-tertiary); font-size: 12px; flex-shrink: 0; }
  &:focus-within { border-color: var(--text-tertiary); }
`;
const SearchInput = styled.input `
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: 13px;
  width: 100%;
  outline: none;
  &::placeholder { color: var(--text-tertiary); }
`;
const CheckboxWrapper = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-secondary);
  white-space: nowrap;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: var(--text-primary);
  }

  label {
    cursor: pointer;
    user-select: none;
  }
`;
const LogsList = styled.div `
  max-height: 65vh;
  overflow-y: auto;
`;
const LogEntryRow = styled.div `
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: background 0.12s;
  &:hover { background: var(--bg-tertiary); }
  &:last-child { border-bottom: none; }
  ${p => p.$level === 'error' && css `background: rgba(239, 68, 68, 0.03); &:hover { background: rgba(239, 68, 68, 0.06); }`}
  ${p => p.$level === 'warn' && css `background: rgba(245, 158, 11, 0.02); &:hover { background: rgba(245, 158, 11, 0.05); }`}
`;
const LogRowMain = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  gap: 16px;
  min-height: 44px;
`;
const LogRowLeft = styled.div `
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;
const LogRowRight = styled.div `
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;
const ExpandIcon = styled.span `
  color: var(--text-tertiary);
  font-size: 9px;
  flex-shrink: 0;
  width: 12px;
  transition: transform 0.15s;
  transform: ${p => p.$expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
  display: flex;
  align-items: center;
`;
const LevelDot = styled.span `
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$color};
  flex-shrink: 0;
`;
const ActionIcon = styled.span `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  flex-shrink: 0;
  font-size: 12px;
  background: ${p => getCatColor(p.$category).bg};
  color: ${p => getCatColor(p.$category).text};
`;
const CategoryPill = styled.span `
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid;
  flex-shrink: 0;
`;
const LogContentWrap = styled.div `
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;
const LogContentRow = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  flex-wrap: nowrap;
  overflow: hidden;
`;
const MethodChip = styled.span `
  display: inline-flex;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 700;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  ${p => {
    switch (p.$method) {
        case 'GET': return 'background: rgba(34,197,94,0.12); color: #16a34a;';
        case 'POST': return 'background: rgba(59,130,246,0.12); color: #2563eb;';
        case 'PUT':
        case 'PATCH': return 'background: rgba(245,158,11,0.12); color: #d97706;';
        case 'DELETE': return 'background: rgba(239,68,68,0.12); color: #dc2626;';
        default: return 'background: rgba(107,114,128,0.12); color: #6b7280;';
    }
}}
`;
const EndpointText = styled.span `
  color: var(--text-primary);
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const StatusChip = styled.span `
  display: inline-flex;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  flex-shrink: 0;
  ${p => p.$ok
    ? 'background: rgba(34,197,94,0.12); color: #16a34a;'
    : 'background: rgba(239,68,68,0.12); color: #dc2626;'}
`;
const DurationText = styled.span `
  font-size: 12px;
  font-weight: 500;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  flex-shrink: 0;
`;
const ActionLabel = styled.span `
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  flex-shrink: 0;
`;
const RoleChip = styled.span `
  display: inline-flex;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: rgba(107, 114, 128, 0.1);
  color: var(--text-tertiary);
  border: 1px solid var(--border-color);
`;
const UserLabel = styled.span `
  font-size: 12px;
  color: var(--text-tertiary);
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const TimestampLabel = styled.span `
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--text-tertiary);
  white-space: nowrap;
  svg { font-size: 10px; opacity: 0.6; }
`;
// ─── Expanded Detail Panel ───────────────────────────────────────
const LogExpandedDetails = styled.div `
  margin: 0 20px 12px 20px;
  padding: 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: default;
`;
const DetailGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;
const DetailSection = styled.div ``;
const DetailHeading = styled.div `
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-tertiary);
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border-color);
`;
const DetailRow = styled.div `
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 4px 0;
  font-size: 13px;
`;
const DetailKey = styled.span `
  color: var(--text-tertiary);
  min-width: 80px;
  flex-shrink: 0;
  font-size: 12px;
`;
const DetailVal = styled.span `
  color: var(--text-primary);
  word-break: break-all;
  code {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    background: var(--bg-secondary);
    padding: 1px 5px;
    border-radius: 3px;
    font-size: 12px;
  }
`;
const MessageBlock = styled.div `
  margin-top: 16px;
`;
const MessageText = styled.div `
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
  padding: 8px 12px;
  background: var(--bg-secondary);
  border-radius: 4px;
`;
const MetadataBlock = styled.div `
  margin-top: 16px;
`;
const MetadataPre = styled.pre `
  background: var(--bg-secondary);
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 12px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  color: var(--text-secondary);
  max-height: 200px;
  overflow-y: auto;
  line-height: 1.5;
  margin: 0;
`;
// ─── Empty / Error States ────────────────────────────────────────
const EmptyState = styled.div `
  padding: 64px 20px;
  text-align: center;
  color: var(--text-tertiary);
  svg { font-size: 40px; margin-bottom: 16px; opacity: 0.4; }
  h3 { color: var(--text-primary); margin-bottom: 8px; font-size: 16px; }
  p { font-size: 14px; }
`;
// ─── Pagination ──────────────────────────────────────────────────
const PaginationBar = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  border-top: 1px solid var(--border-color);
  flex-wrap: wrap;
  gap: 12px;
`;
const PaginationInfo = styled.span `
  font-size: 13px;
  color: var(--text-tertiary);
`;
const PageButtons = styled.div `
  display: flex;
  gap: 4px;
  align-items: center;
`;
const PageBtn = styled.button `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  font-size: 13px;
  font-weight: ${p => p.$active ? '600' : '400'};
  border: 1px solid ${p => p.$active ? 'var(--text-primary)' : 'var(--border-color)'};
  border-radius: 6px;
  background: ${p => p.$active ? 'var(--text-primary)' : 'transparent'};
  color: ${p => p.$active ? 'var(--bg-primary)' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) { border-color: var(--text-primary); color: ${p => p.$active ? 'var(--bg-primary)' : 'var(--text-primary)'}; }
  &:disabled { opacity: 0.3; cursor: not-allowed; }
  svg { width: 12px; height: 12px; }
`;
const PageEllipsis = styled.span `
  padding: 0 4px;
  color: var(--text-tertiary);
  font-size: 13px;
`;
export default SystemLogs;
